"use client";

/**
 * The signed-in hub's first screen in one read: GET /api/me/hub (backend
 * routes/personal ``my_hub``), 2026-10-23.
 *
 * From Baku every API request is a round trip of about 125 ms (more on a
 * phone), and the hub's first screen waited for a chain of them: /auth/me,
 * then /me/home, then the hero's listings, then the layout and the
 * widgets. With the session cookie the page starts this read beside
 * /auth/me (the cookie is all the API needs), and its parts answer the
 * readers that would each have asked on their own:
 *
 *   home        usePersonal's store (/me/home)
 *   market      the hero's listings (HubHero), when its query is the same
 *   yield_pick  an investor's spotlight pick (HubHero)
 *   onboarding  shared("/auth/onboarding"): the hub's extras, the roadmap
 *   offer       shared("/feedback-offer"): the card above the widgets
 *   layout      the account's saved board (lib/hub-layout)
 *   properties  the properties store (lib/use-properties)
 *   tracked     the tracked-listings store (lib/use-tracked), agents only
 *
 * A part answers only for the account the read was for and only for a
 * short while (FRESH_MS); anything else reads its own endpoint as before.
 */
import { API_BASE } from "@/lib/api";
import { getClientId } from "@/lib/client-id";
import { seedShared } from "@/lib/shared-get";
import type { PersonalHome } from "@/lib/use-personal";
import type { ListingCard } from "@/types";

export interface HubBoot {
  user_id: string;
  /** The account's stored goal the payload was read for. */
  goal: string | null;
  home: PersonalHome;
  /** The hero's newest 12 and the count, for the query in ``params``. */
  market: { params: Record<string, string | string[]>; total: number; results: ListingCard[] } | null;
  /** An investor's spotlight pick for the query in ``ids``/``budget``. */
  yield_pick: { ids: string; budget: string; item: { card: ListingCard; yield_pct: number; projected_rent: number; payback_years: number | null } | null } | null;
  onboarding: Record<string, boolean> | null;
  offer: unknown;
  layout: Record<string, unknown> | null;
  properties: { items: unknown[]; limit: number; rollup: unknown } | null;
  tracked: unknown;
}

/** How long a landed read stands in for the separate ones: the first
 *  screen, and Strict Mode's second mount in dev. A return to the hub
 *  later reads fresh. */
const FRESH_MS = 30_000;
/** A read that never answers must not hold the hub: past this its
 *  readers ask their own endpoints, as they did before it existed. */
const GIVE_UP_MS = 12_000;

type Boot = { promise: Promise<HubBoot | null>; value: HubBoot | null | undefined; at: number };
let cur: Boot | null = null;
const listeners = new Set<(b: HubBoot) => void>();

const fresh = (b: Boot) => b.value === undefined || Date.now() - b.at < FRESH_MS;

/** Starts the read (one at a time; a fresh one is reused). */
export function startHubBoot(): Promise<HubBoot | null> {
  if (cur && fresh(cur)) return cur.promise;
  const boot: Boot = { promise: Promise.resolve(null), value: undefined, at: Date.now() };
  const url = new URL(`${API_BASE}/me/hub`, window.location.origin);
  // The device's id in the query, not a header: a header would make the
  // browser ask the API for permission first (a CORS preflight).
  const cid = getClientId();
  if (cid) url.searchParams.set("cid", cid);
  const ctl = typeof AbortController === "undefined" ? null : new AbortController();
  const giveUp = ctl ? setTimeout(() => ctl.abort(), GIVE_UP_MS) : null;
  boot.promise = fetch(url.toString(), ctl ? { credentials: "include", signal: ctl.signal } : { credentials: "include" })
    .then((r) => (r.ok ? (r.json() as Promise<HubBoot>) : null))
    .catch(() => null)
    .then((v) => {
      if (giveUp) clearTimeout(giveUp);
      boot.value = v;
      boot.at = Date.now();
      if (v) listeners.forEach((l) => { try { l(v); } catch { /* one store's problem stays its own */ } });
      return v;
    });
  cur = boot;
  // The two shared reads it carries answer from it while it is in
  // flight; without an answer (signed out, an error) their callers read
  // their own.
  seedShared("/auth/onboarding", boot.promise.then((v) => v?.onboarding ?? undefined));
  seedShared("/feedback-offer", boot.promise.then((v) => v?.offer ?? undefined));
  return boot.promise;
}

/** The read in flight, or landed within FRESH_MS; else null. */
export function hubBootPending(): Promise<HubBoot | null> | null {
  return cur && fresh(cur) ? cur.promise : null;
}

/** The landed read (for ``uid`` when given) while fresh, else null:
 *  synchronous, so a first render can start from it. */
export function peekHubBoot(uid?: string | null): HubBoot | null {
  const v = cur && fresh(cur) ? cur.value : null;
  if (!v || (uid && v.user_id !== uid)) return null;
  return v;
}

/** ``fn`` runs with every read that lands, and at once with a fresh one
 *  that has landed already. */
export function onHubBoot(fn: (b: HubBoot) => void): void {
  listeners.add(fn);
  const v = peekHubBoot();
  if (v) fn(v);
}
